import Keycloak from 'keycloak-js';
import { LocalStorageService } from './localStorageService';
import { TokenService } from './tokenService';
import { AuthValidationService } from './authValidationService';

/**
 * Rozszerzony interfejs KeycloakTokenParsed zawierający pola używane w aplikacji
 */
interface ExtendedKeycloakTokenParsed {
  exp?: number;
  iat?: number;
  nonce?: string;
  sub?: string;
  session_state?: string;
  realm_access?: { roles: string[] };
  resource_access?: Record<string, { roles: string[] }>;
  // Dodatkowe pola z profilu użytkownika
  preferred_username?: string;
  email?: string;
  given_name?: string;
  family_name?: string;
  name?: string;
}

/**
 * Konfiguracja Keycloak
 */
const keycloakConfig = {
  url: process.env.REACT_APP_KEYCLOAK_URL || 'https://keycloak.ustalone.com',
  realm: process.env.REACT_APP_KEYCLOAK_REALM || 'crm-realm',
  clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID || 'crm-client'
};

/**
 * Instancja Keycloak
 */
export const keycloak = new Keycloak(keycloakConfig);

/**
 * Flaga określająca czy Keycloak został już zainicjalizowany
 */
let keycloakInitialized = false;

/**
 * Serwis do zarządzania autoryzacją przez Keycloak
 */
export const KeycloakService = {
  /**
   * Inicjalizacja Keycloak
   */
  init: async () => {
    // Jeśli Keycloak został już zainicjalizowany, zwróć tylko stan uwierzytelnienia
    if (keycloakInitialized) {
      return keycloak.authenticated || false;
    }

    try {
      const authenticated = await (keycloak as any).init({
        onLoad: 'check-sso',
        checkLoginIframe: false,
        enableLogging: true,
        silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
        enableThirdPartyCookies: false,
         pkceMethod: 'S256',
         // NOWE: dodaj konfigurację postLogoutRedirectUri
         postLogoutRedirectUri: window.location.origin + '/',
         // Dodanie obsługi tokenów z localStorage/sessionStorage
         token: localStorage.getItem('kc_token') || sessionStorage.getItem('kc_token'),
         refreshToken: localStorage.getItem('kc_refreshToken') || sessionStorage.getItem('kc_refreshToken')
      });

      // Ustaw flagę inicjalizacji na true
      keycloakInitialized = true;

      // Dodanie obsługi tokenu - zapisanie tokenu w localStorage/sessionStorage
      if (authenticated && keycloak.token) {
        LocalStorageService.saveToken(keycloak.token);
        if (keycloak.refreshToken) {
          LocalStorageService.saveRefreshToken(keycloak.refreshToken);
        }

        // Ustawienie nasłuchiwania na zmiany tokenu
        keycloak.onTokenExpired = () => {
          console.log('Token wygasł, próba odświeżenia...');
          KeycloakService.updateToken(0) // Wymuszamy odświeżenie z minValidity = 0
            .then(refreshed => {
              if (refreshed) {
                console.log('Token został pomyślnie odświeżony po wygaśnięciu');
              } else {
                console.warn('Token nie został odświeżony - może być nadal ważny lub wystąpił problem');
                // Sprawdzamy czy token jest rzeczywiście ważny
                if (!KeycloakService.isTokenValid(0)) {
                  console.error('Token jest nieważny i nie można go odświeżyć - automatyczne wylogowanie');
                  setTimeout(() => {
                    KeycloakService.logout();
                  }, 1000);
                }
              }
            })
            .catch(error => {
              console.error('Błąd podczas odświeżania wygasłego tokenu:', error);
              // Błąd zostanie obsłużony w updateToken() - automatyczne wylogowanie jeśli potrzebne
            });
        };

        // Dodanie nasłuchiwania na aktualizację tokenu
        keycloak.onAuthRefreshSuccess = () => {
          console.log('Token został odświeżony');
          if (keycloak.token) {
            LocalStorageService.saveToken(keycloak.token);
          }
          if (keycloak.refreshToken) {
            LocalStorageService.saveRefreshToken(keycloak.refreshToken);
          }
        };
      }

      console.log("Ustawiony silentCheckSsoRedirectUri:", window.location.origin + '/silent-check-sso.html');
      console.log(`Użytkownik ${authenticated ? 'jest zalogowany' : 'nie jest zalogowany'}`);
      return authenticated;
    } catch (error) {
      console.error('Błąd inicjalizacji Keycloak:', error);
      if (error instanceof Error) {
        console.error('Szczegóły błędu:', error.message, error.stack);
      }
      return false;
    }
  },

  /**
   * Logowanie użytkownika przez przekierowanie do strony Keycloak
   * @param redirectUri - opcjonalny URI przekierowania po zalogowaniu
   */
  login: (redirectUri?: string) => {
    return keycloak.login({
      redirectUri: redirectUri || window.location.origin + '/'
    });
  },


  /**
   * Wylogowanie użytkownika
   */
  logout: async (): Promise<void> => {
    try {
      if (keycloak && keycloak.authenticated) {
        // Wyczyść lokalne dane
        localStorage.removeItem('kc_token');
        sessionStorage.removeItem('kc_token');
        LocalStorageService.clearTokens();
        
        // Przekieruj na stronę główną zamiast obecnej strony
        await keycloak.logout({
          redirectUri: window.location.origin + '/'  // Zmiana tutaj!
        });
      }
    } catch (error) {
      console.error('Błąd podczas wylogowania:', error);
      // Fallback - wyczyść dane i przekieruj manualnie
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/';
    }
  },

  /**
   * Ciche wylogowanie - omija Keycloak logout endpoint
   */
  silentLogout: (): void => {
    console.log('Wykonuję ciche wylogowanie');
    
    try {
      // Wyczyść wszystkie dane uwierzytelnienia
      if (keycloak) {
        keycloak.authenticated = false;
        keycloak.token = undefined;
        keycloak.refreshToken = undefined;
        keycloak.idToken = undefined;
        keycloak.tokenParsed = undefined;
      }
      
      // Wyczyść storage
      localStorage.removeItem('kc_token');
      sessionStorage.removeItem('kc_token');
      localStorage.removeItem('kc_refreshToken');
      sessionStorage.removeItem('kc_refreshToken');
      LocalStorageService.clearTokens();
      
      // Przekieruj bez wywoływania Keycloak logout endpoint
      window.location.href = '/';
      
    } catch (error) {
      console.error('Błąd podczas cichego wylogowania:', error);
      // Fallback
      window.location.href = '/';
    }
  },

  /**
   * Odświeżenie tokenu
   * @param minValidity - minimalna ważność tokenu w sekundach (domyślnie 5)
   * @returns Promise<boolean> - informacja czy token został odświeżony
   */
  updateToken: (minValidity: number = 5) => {
    // Sprawdzamy czy token istnieje
    if (!keycloak.token) {
      // Próbujemy odzyskać token z localStorage
      const storedToken = LocalStorageService.getToken();
      const storedRefreshToken = LocalStorageService.getRefreshToken();

      if (storedToken) {
        console.log('Odzyskano token z localStorage/sessionStorage');
        keycloak.token = storedToken;
        if (storedRefreshToken) {
          keycloak.refreshToken = storedRefreshToken;

          // Sprawdzamy czy token nie wygasł już dawno
          const timeUntilExpiration = TokenService.getTimeUntilExpiration(storedToken);
          if (timeUntilExpiration !== null) {
            const secondsUntilExpiration = Math.round(timeUntilExpiration / 1000);
            const safetyMarginSeconds = 30;

            if (timeUntilExpiration <= 0) {
              console.warn(`Token wygasł ${Math.abs(secondsUntilExpiration)} sekund temu, próba odświeżenia...`);
            } else if (timeUntilExpiration <= safetyMarginSeconds * 1000) {
              console.warn(`Token wygasa za ${secondsUntilExpiration} sekund (w marginesie bezpieczeństwa), próba odświeżenia...`);
            } else {
              console.log(`Token wygasa za ${secondsUntilExpiration} sekund`);
            }
          } else {
            console.warn('Nie udało się zdekodować tokenu');
          }
        } else {
          console.warn('Brak refresh tokenu w localStorage/sessionStorage');
        }
      } else {
        console.warn('Brak tokenu w localStorage/sessionStorage');
        // Brak tokenu, nie można odświeżyć
        return Promise.resolve(false);
      }
    }

    return keycloak.updateToken(minValidity)
      .then((refreshed) => {
        if (refreshed) {
          console.log('Token został odświeżony');
          if (keycloak.token) {
            LocalStorageService.saveToken(keycloak.token);
          }
          if (keycloak.refreshToken) {
            LocalStorageService.saveRefreshToken(keycloak.refreshToken);
          }
        } else {
          // Token był jeszcze ważny, nie wymagał odświeżenia
          // Sprawdzamy rzeczywisty czas wygaśnięcia dla debugowania
          const token = KeycloakService.getToken();
          if (token) {
            const timeUntilExpiration = TokenService.getTimeUntilExpiration(token);
            if (timeUntilExpiration !== null) {
              const secondsUntilExpiration = Math.round(timeUntilExpiration / 1000);
              console.log(`Token jest nadal ważny, nie wymagał odświeżenia (wygasa za ${secondsUntilExpiration} sekund)`);
            } else {
              console.log('Token jest nadal ważny, nie wymagał odświeżenia (brak informacji o wygaśnięciu)');
            }
          } else {
            console.log('Token jest nadal ważny, nie wymagał odświeżenia (brak tokenu)');
          }
        }
        return refreshed;
      }).catch((error) => {
        console.error('Błąd odświeżania tokenu:', error);
        // Sprawdzamy, czy błąd wskazuje na wygaśnięcie tokenu odświeżającego
        // Jeśli tak, trzeba wymusić ponowne logowanie
        if (error && typeof error === 'object' && 'error' in error && error.error === 'invalid_grant') {
          console.warn('Refresh token wygasł, wymagane ponowne logowanie');
          // Automatyczne wylogowanie gdy refresh token jest nieprawidłowy
          setTimeout(() => {
            console.log('Automatyczne wylogowanie z powodu wygaśnięcia refresh token');
            KeycloakService.logout();
          }, 1000); // Krótkie opóźnienie aby pozwolić na zakończenie bieżących operacji
        } else if (error && (error.toString().includes('Failed to refresh token') || 
                           error.toString().includes('Token refresh failed'))) {
          // Inne błędy odświeżania tokenu - również wylogowujemy
          console.warn('Nie udało się odświeżyć tokenu, automatyczne wylogowanie');
          setTimeout(() => {
            console.log('Automatyczne wylogowanie z powodu niepowodzenia odświeżenia tokenu');
            KeycloakService.logout();
          }, 1000);
        }
        return false;
      });
  },

  /**
   * Sprawdzenie czy token jest rzeczywiście ważny (z marginesem bezpieczeństwa)
   */
  isTokenValid: (safetyMarginSeconds: number = 30) => {
    return TokenService.isTokenValid(keycloak.token, safetyMarginSeconds);
  },

  /**
   * Pobranie aktualnego tokenu
   */
  getToken: () => keycloak.token,

  /**
   * Sprawdzenie czy użytkownik jest zalogowany
   */
  isLoggedIn: () => !!keycloak.token,

  /**
   * Pobranie nazwy użytkownika
   */
  getUsername: () => {
    return TokenService.getUsername(keycloak.token);
  },

  /**
   * Sprawdzenie czy użytkownik ma określoną rolę
   */
  hasRole: (role: string) => {
    return keycloak.hasRealmRole(role);
  },

  /**
   * Pobranie informacji o użytkowniku
   */
  getUserInfo: () => {
    return TokenService.getUserInfo(keycloak.token, keycloak.realmAccess?.roles || []);
  },

  /**
   * Sprawdza czy token wygasł i wymusza wylogowanie jeśli tak
   * @returns boolean - true jeśli token jest ważny, false jeśli wygasł i użytkownik został wylogowany
   */
  checkTokenExpirationAndLogout: () => {
    if (!keycloak.token) {
      return false;
    }

    // Use AuthValidationService for comprehensive token validation
    const validation = AuthValidationService.validateTokenForLogout(keycloak.token, 30);
    
    if (!validation.isValid) {
      console.warn(`${validation.message} - automatyczne wylogowanie`);

      if (validation.shouldLogout) {
        // Próbujemy odświeżyć token przed wylogowaniem
        KeycloakService.updateToken(60)
          .then(refreshed => {
            if (!refreshed && !KeycloakService.isTokenValid(30)) {
              console.error('Nie udało się odświeżyć wygasłego tokenu - wylogowanie');
              setTimeout(() => {
                KeycloakService.logout();
              }, 1000);
            }
          })
          .catch(() => {
            console.error('Błąd odświeżania wygasłego tokenu - wylogowanie');
            setTimeout(() => {
              KeycloakService.logout();
            }, 1000);
          });
      }

      return false;
    }

    return true;
  },

  /**
   * Rozpoczyna okresowe sprawdzanie wygaśnięcia tokenu
   */
  startTokenExpirationCheck: () => {
    // Sprawdzamy token co minutę
    const checkInterval = setInterval(() => {
      if (KeycloakService.isLoggedIn()) {
        KeycloakService.checkTokenExpirationAndLogout();
      } else {
        // Jeśli użytkownik nie jest zalogowany, zatrzymujemy sprawdzanie
        clearInterval(checkInterval);
      }
    }, 60000); // 1 minuta

    return checkInterval;
  },

  /**
   * Ustawia tokeny otrzymane z logowania REST API
   */
  setTokensFromLogin: async (tokens: {
    access_token: string;
    refresh_token: string;
    id_token?: string;
  }): Promise<void> => {
    try {
      // Ustawienie tokenów w instancji Keycloak
      keycloak.token = tokens.access_token;
      keycloak.refreshToken = tokens.refresh_token;
      keycloak.idToken = tokens.id_token;
      keycloak.authenticated = true;

      // Parsowanie tokenów
      keycloak.tokenParsed = JSON.parse(atob(tokens.access_token.split('.')[1]));
      if (tokens.id_token) {
        keycloak.idTokenParsed = JSON.parse(atob(tokens.id_token.split('.')[1]));
      }

      // Zapisanie w storage
      LocalStorageService.saveToken(tokens.access_token);
      LocalStorageService.saveRefreshToken(tokens.refresh_token);

      // Ustawienie flag
      keycloakInitialized = true;

      // Ustawienie obsługi wygaśnięcia tokenu
      KeycloakService.setupTokenHandlers();

      console.log('Tokeny zostały ustawione po logowaniu REST API');
    } catch (error) {
      console.error('Błąd podczas ustawiania tokenów:', error);
      throw error;
    }
  },

  /**
   * Konfiguruje handlery dla tokenów
   */
  setupTokenHandlers: () => {
    keycloak.onTokenExpired = () => {
      console.log('Token wygasł, próba odświeżenia...');
      KeycloakService.updateToken(0)
        .then(refreshed => {
          if (!refreshed) {
            console.error('Nie można odświeżyć tokenu - wylogowanie');
            KeycloakService.logout();
          }
        })
        .catch(error => {
          console.error('Błąd odświeżania tokenu:', error);
          KeycloakService.logout();
        });
    };

    keycloak.onAuthRefreshSuccess = () => {
      console.log('Token został odświeżony');
      if (keycloak.token) {
        LocalStorageService.saveToken(keycloak.token);
      }
      if (keycloak.refreshToken) {
        LocalStorageService.saveRefreshToken(keycloak.refreshToken);
      }
    };
  }
};
